import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { ScrollArea } from "../ui/scroll-area";
import { Badge } from "../ui/badge";
import {
  Sparkles,
  Send,
  Loader2,
  User,
  Bot,
  Trash2,
  Copy,
  Check,
  Minimize2,
  Maximize2,
  Settings,
  Brain,
  ThumbsUp,
  ThumbsDown,
  HelpCircle,
  X,
  GripHorizontal,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

// Escape HTML before injecting via dangerouslySetInnerHTML. AI/ERP text can
// contain markup (e.g. a customer name or lead note with "<img onerror=...>"),
// which would otherwise execute as XSS. "**" is not HTML-special, so the bold
// markdown regex still works on the escaped output.
const escapeHtml = (str) =>
  String(str).replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c])
  );

// Parse markdown table to HTML
const parseMarkdownTable = (tableText) => {
  const lines = tableText.trim().split('\n').filter(line => line.trim());
  if (lines.length < 2) return null;

  const parseRow = (line) => {
    return line.split('|').map(cell => cell.trim()).filter((cell, idx, arr) => idx > 0 && idx < arr.length);
  };

  const headerCells = parseRow(lines[0]);
  if (headerCells.length === 0) return null;

  // Skip separator line (line with dashes)
  const dataStartIndex = lines[1].includes('---') ? 2 : 1;
  const dataRows = lines.slice(dataStartIndex).map(parseRow);

  return (
    <div className="overflow-x-auto my-2">
      <table className="min-w-full text-xs border-collapse">
        <thead>
          <tr className="bg-muted/50">
            {headerCells.map((cell, i) => (
              <th key={i} className="border border-border px-2 py-1 text-left font-semibold">
                {cell.replace(/\*\*/g, '')}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {dataRows.map((row, rowIdx) => (
            <tr key={rowIdx} className={rowIdx % 2 === 0 ? 'bg-background' : 'bg-muted/30'}>
              {row.map((cell, cellIdx) => (
                <td key={cellIdx} className="border border-border px-2 py-1">
                  {cell.replace(/\*\*/g, '')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Message component (single local BANA AI)
const ChatMessage = ({ message, onCopy, onVote, token }) => {
  const [copied, setCopied] = useState(false);
  const [vote, setVote] = useState(message.vote || null); // 'up', 'down', or null
  const isUser = message.role === "user";
  const displayContent = message.content;

  // Detect if AI is asking a clarifying question
  const isIntern = !isUser && displayContent && (
    /\?\s*$/.test(displayContent.trim()) &&
    /(which|what|could you|can you|do you mean|please clarify|please specify|are you|did you mean)/i.test(displayContent)
  );

  const handleCopy = () => {
    navigator.clipboard.writeText(displayContent || "");
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleVote = async (type) => {
    const newVote = vote === type ? null : type; // toggle
    setVote(newVote);
    if (onVote) onVote(message, newVote);

    // Send feedback to backend
    if (token) {
      try {
        await axios.post(`${API}/ai/feedback`, {
          user_message: message.userMessage || '',
          ai_response: displayContent,
          vote: newVote,
          timestamp: message.timestamp,
        }, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch (e) {
        // Silent fail
      }
    }
  };

  // Advanced markdown formatting with table support
  const formatContent = (content) => {
    const elements = [];
    let currentIndex = 0;

    // Split content into lines for processing
    const lines = content.split('\n');
    let i = 0;

    while (i < lines.length) {
      const line = lines[i];

      // Check for table start (line with | characters)
      if (line.includes('|') && line.trim().startsWith('|')) {
        // Collect all table lines
        const tableLines = [];
        while (i < lines.length && lines[i].includes('|')) {
          tableLines.push(lines[i]);
          i++;
        }
        if (tableLines.length >= 2) {
          const table = parseMarkdownTable(tableLines.join('\n'));
          if (table) {
            elements.push(<div key={`table-${currentIndex++}`}>{table}</div>);
            continue;
          }
        }
        // If not a valid table, process as regular lines
        i -= tableLines.length;
      }

      // Check for code blocks
      if (line.trim().startsWith('```')) {
        const codeLines = [line];
        i++;
        while (i < lines.length && !lines[i].trim().startsWith('```')) {
          codeLines.push(lines[i]);
          i++;
        }
        if (i < lines.length) {
          codeLines.push(lines[i]);
          i++;
        }
        const code = codeLines.slice(1, -1).join('\n');
        elements.push(
          <pre key={`code-${currentIndex++}`} className="bg-muted p-2 rounded-md text-xs overflow-x-auto my-2 font-mono">
            <code>{code}</code>
          </pre>
        );
        continue;
      }

      // Check for headers
      if (line.startsWith('## ')) {
        elements.push(
          <h2 key={`h2-${currentIndex++}`} className="text-base font-bold mt-3 mb-1 flex items-center gap-1">
            {line.substring(3).replace(/\*\*/g, '')}
          </h2>
        );
        i++;
        continue;
      }

      if (line.startsWith('### ')) {
        elements.push(
          <h3 key={`h3-${currentIndex++}`} className="text-sm font-semibold mt-2 mb-1">
            {line.substring(4).replace(/\*\*/g, '')}
          </h3>
        );
        i++;
        continue;
      }

      // Check for bullet points
      if (/^[\-\*•]\s/.test(line.trim())) {
        const bulletContent = line.trim().replace(/^[\-\*•]\s/, '');
        let formatted = escapeHtml(bulletContent).replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        elements.push(
          <div key={`bullet-${currentIndex++}`} className="flex items-start gap-1 ml-2">
            <span className="text-primary">•</span>
            <span dangerouslySetInnerHTML={{ __html: formatted }} />
          </div>
        );
        i++;
        continue;
      }

      // Regular text with bold formatting
      if (line.trim()) {
        let formatted = escapeHtml(line).replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        elements.push(
          <p key={`p-${currentIndex++}`} className="my-0.5" dangerouslySetInnerHTML={{ __html: formatted }} />
        );
      } else {
        // Empty line - add small spacing
        elements.push(<div key={`space-${currentIndex++}`} className="h-1" />);
      }
      i++;
    }

    return elements;
  };

  return (
    <div className={`flex gap-3 ${isUser ? "flex-row-reverse" : ""}`}>
      <div
        className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
          isUser ? "bg-primary text-primary-foreground"
            : isIntern ? "bg-gradient-to-br from-amber-500 to-orange-500 text-white"
              : "bg-gradient-to-br from-purple-500 to-blue-500 text-white"
        }`}
      >
        {isUser ? <User className="h-4 w-4" /> : isIntern ? <HelpCircle className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
      </div>
      <div className={`flex-1 ${isUser ? "text-right" : ""}`}>
        {/* Clarifying-question tag */}
        {isIntern && (
          <div className="mb-1">
            <Badge variant="outline" className="text-[9px] border-amber-400 text-amber-600 dark:text-amber-400">
              <HelpCircle className="h-2.5 w-2.5 mr-0.5" />
              Asking for clarification
            </Badge>
          </div>
        )}
        <div
          className={`inline-block max-w-[85%] p-3 rounded-xl text-sm ${
            isUser
              ? "bg-primary text-primary-foreground rounded-br-sm"
              : isIntern
                ? "bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-bl-sm"
                : "bg-muted rounded-bl-sm"
          }`}
        >
          <div className="whitespace-pre-wrap break-words">
            {formatContent(displayContent || "")}
          </div>
        </div>
        {!isUser && (
          <div className="flex items-center gap-1 mt-1">
            {/* Vote buttons */}
            <Button
              variant="ghost"
              size="icon"
              className={`h-6 w-6 ${vote === 'up' ? 'text-green-500' : 'text-muted-foreground hover:text-green-500'}`}
              onClick={() => handleVote('up')}
              title="Good response"
            >
              <ThumbsUp className="h-3 w-3" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className={`h-6 w-6 ${vote === 'down' ? 'text-red-500' : 'text-muted-foreground hover:text-red-500'}`}
              onClick={() => handleVote('down')}
              title="Bad response — AI will try differently next time"
            >
              <ThumbsDown className="h-3 w-3" />
            </Button>
            <span className="w-px h-3 bg-border mx-0.5" />
            {/* Copy button */}
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={handleCopy}
            >
              {copied ? (
                <Check className="h-3 w-3 text-green-500" />
              ) : (
                <Copy className="h-3 w-3 text-muted-foreground" />
              )}
            </Button>
            <span className="text-[10px] text-muted-foreground">
              {new Date(message.timestamp).toLocaleTimeString("en-IN", {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </span>
            {message.confidence && message.model?.includes('BANA') && (
              <span className="text-[10px] text-green-600 dark:text-green-400 font-medium">
                {Math.round(message.confidence * 100)}% match
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ── Floating window geometry ─────────────────────────────────────────────────
// The chat is a movable, resizable window (not a docked sheet) so it can sit
// beside whatever page the user is working on. Last rect is remembered per
// device and clamped back into view on load / viewport resize.
const RECT_KEY = "ai_chat_window_rect";
const MIN_W = 340;
const MIN_H = 360;
const EDGE = 4; // keep the window this many px inside the viewport

const defaultRect = () => {
  const w = Math.min(420, window.innerWidth - 32);
  const h = Math.min(620, window.innerHeight - 120);
  return { x: window.innerWidth - w - 24, y: 96, w, h };
};

const clampRect = (r) => {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const w = Math.min(Math.max(MIN_W, r.w), vw - EDGE * 2);
  const h = Math.min(Math.max(MIN_H, r.h), vh - EDGE * 2);
  return {
    w,
    h,
    x: Math.min(Math.max(EDGE, r.x), vw - w - EDGE),
    y: Math.min(Math.max(EDGE, r.y), vh - h - EDGE),
  };
};

const readRect = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(RECT_KEY) || "null");
    if (saved && ["x", "y", "w", "h"].every((k) => typeof saved[k] === "number")) return clampRect(saved);
  } catch { /* storage unavailable — fall through */ }
  return clampRect(defaultRect());
};

const saveRect = (r) => {
  try { localStorage.setItem(RECT_KEY, JSON.stringify(r)); } catch { /* ignore */ }
};

// Resize handles: the direction letters say which edges move.
const HANDLES = [
  { dir: "n", cls: "top-0 left-3 right-3 h-1.5 cursor-ns-resize" },
  { dir: "s", cls: "bottom-0 left-3 right-3 h-1.5 cursor-ns-resize" },
  { dir: "w", cls: "left-0 top-3 bottom-3 w-1.5 cursor-ew-resize" },
  { dir: "e", cls: "right-0 top-3 bottom-3 w-1.5 cursor-ew-resize" },
  { dir: "nw", cls: "top-0 left-0 h-3 w-3 cursor-nwse-resize" },
  { dir: "ne", cls: "top-0 right-0 h-3 w-3 cursor-nesw-resize" },
  { dir: "sw", cls: "bottom-0 left-0 h-3 w-3 cursor-nesw-resize" },
  { dir: "se", cls: "bottom-0 right-0 h-3 w-3 cursor-nwse-resize" },
];

// Suggested prompts
// BANA AI v0 only does maths (backend services/ai/engine.js) — suggest that.
const SUGGESTED_PROMPTS = [
  "25% of 4,80,000",
  "1250 * 18 / 100",
  "5 lakh divided by 12",
  "square root of 144",
  "1000 + 18%",
];

export default function AIChatDialog({ open, onOpenChange }) {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [rect, setRect] = useState(readRect);
  // Rect to return to when un-maximizing (null = not maximized).
  const [restoreRect, setRestoreRect] = useState(null);
  const [interacting, setInteracting] = useState(false);
  const gestureRef = useRef(null);
  const rectRef = useRef(rect);
  rectRef.current = rect;
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Always use full access mode
  const includeActivity = true;
  const includeStats = true;

  // Auto-scroll to bottom
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Focus input and load stored history when dialog opens
  useEffect(() => {
    if (open) {
      if (inputRef.current) {
        setTimeout(() => inputRef.current?.focus(), 100);
      }
      // Load stored chat history from server (persisted across sessions)
      if (messages.length === 0 && token) {
        axios.get(`${API}/ai/chat-history`, {
          headers: { Authorization: `Bearer ${token}` },
        }).then(res => {
          if (res.data?.history?.length > 0) {
            setMessages(res.data.history);
          }
        }).catch(() => {});
      }
    }
  }, [open]);

  // Keep the window reachable when the browser window shrinks.
  useEffect(() => {
    const onResize = () => setRect((r) => clampRect(r));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // One pointer-gesture engine for both move ("move") and resize (edge dirs).
  const startGesture = (e, mode) => {
    if (e.button !== 0) return;
    // Clicks on header buttons must not start a drag.
    if (mode === "move" && e.target.closest("button")) return;
    e.preventDefault();
    gestureRef.current = { mode, sx: e.clientX, sy: e.clientY, start: rectRef.current };
    setInteracting(true);
    setRestoreRect(null); // moving/resizing a maximized window un-maximizes it

    const onMove = (ev) => {
      const g = gestureRef.current;
      if (!g) return;
      const dx = ev.clientX - g.sx;
      const dy = ev.clientY - g.sy;
      const st = g.start;
      if (g.mode === "move") {
        setRect(clampRect({ ...st, x: st.x + dx, y: st.y + dy }));
        return;
      }
      let { x, y, w, h } = st;
      if (g.mode.includes("e")) w = st.w + dx;
      if (g.mode.includes("s")) h = st.h + dy;
      if (g.mode.includes("w")) { w = Math.max(MIN_W, st.w - dx); x = st.x + st.w - w; }
      if (g.mode.includes("n")) { h = Math.max(MIN_H, st.h - dy); y = st.y + st.h - h; }
      setRect(clampRect({ x, y, w, h }));
    };
    const onUp = () => {
      gestureRef.current = null;
      setInteracting(false);
      saveRect(rectRef.current);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  const toggleMaximize = () => {
    if (restoreRect) {
      setRect(restoreRect);
      setRestoreRect(null);
      saveRect(restoreRect);
    } else {
      setRestoreRect(rect);
      setRect(clampRect({ x: 24, y: 24, w: window.innerWidth - 48, h: window.innerHeight - 48 }));
    }
  };

  // Esc closes the window (only while focus is inside it).
  const onWindowKeyDown = (e) => {
    if (e.key === "Escape") onOpenChange(false);
  };

  const sendMessage = useCallback(async (messageText) => {
    if (!messageText.trim() || loading) return;

    const userMessage = {
      role: "user",
      content: messageText.trim(),
      timestamp: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setLoading(true);

    try {
      // Local BANA AI (no external AI API)
      const endpoint = `${API}/ai/chat-llm`;

      const response = await axios.post(
        endpoint,
        {
          message: messageText,
          includeActivity,
          includeStats,
          history: messages.slice(-10),
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const aiMessage = {
        role: "assistant",
        content: response.data.response,
        timestamp: response.data.timestamp || new Date().toISOString(),
        model: response.data.model,
        responseTime: response.data.responseTime,
        confidence: response.data.confidence || null,
        userMessage: messageText.trim(), // Store for feedback context
      };

      setMessages((prev) => [...prev, aiMessage]);
    } catch (error) {
      console.error("AI chat error:", error);
      const errorMessage = error.response?.data?.error || "Failed to get AI response. Please try again.";

      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: `Sorry, I encountered an error: ${errorMessage}`,
          timestamp: new Date().toISOString(),
          isError: true,
        },
      ]);

      if (error.response?.status === 500) {
        toast.error(error.response?.data?.error || "AI service error. Please try again.");
      }
    } finally {
      setLoading(false);
      // Keep typing flow: the caret goes back to the box after every reply.
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [token, loading, messages, includeActivity, includeStats]);

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  const clearChat = () => {
    setMessages([]);
    inputRef.current?.focus();
    // Also clear stored chat history on server
    if (token) {
      axios.delete(`${API}/ai/chat-history`, {
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {});
    }
  };

  const handlePromptClick = (prompt) => {
    sendMessage(prompt);
  };

  // Welcome message
  const welcomeMessage = messages.length === 0;

  return (
    // Kept mounted and toggled via opacity/scale so it animates both in AND
    // out (and keeps scroll position). Hidden state is inert to the pointer.
    <div
      role="dialog"
      aria-label="BANA AI"
      aria-hidden={!open}
      onKeyDown={onWindowKeyDown}
      style={{ left: rect.x, top: rect.y, width: rect.w, height: rect.h }}
      className={`fixed z-[190] flex flex-col overflow-hidden rounded-xl border border-border bg-background shadow-2xl origin-top-right ${
        interacting ? "select-none" : "transition-[opacity,transform,visibility] duration-200 ease-out"
      } ${open ? "opacity-100 scale-100" : "opacity-0 scale-95 pointer-events-none invisible"}`}
    >
        {/* Resize handles */}
        {HANDLES.map((h) => (
          <div
            key={h.dir}
            onPointerDown={(e) => startGesture(e, h.dir)}
            className={`absolute z-10 touch-none ${h.cls}`}
          />
        ))}

        {/* Header — drag handle (double-click to maximize/restore) */}
        <div
          onPointerDown={(e) => startGesture(e, "move")}
          onDoubleClick={(e) => { if (!e.target.closest("button")) toggleMaximize(); }}
          className="px-3 py-2 border-b flex-shrink-0 cursor-move touch-none bg-muted/30"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 font-semibold">
              <GripHorizontal className="h-4 w-4 text-muted-foreground/60" />
              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center">
                <Sparkles className="h-3.5 w-3.5 text-white" />
              </div>
              <div>
                <span className="text-sm">BANA AI</span>
                {(() => {
                  const lastModel = messages.length > 0 ? messages[messages.length - 1].model : null;
                  const isBana = lastModel?.includes('BANA AI') && !lastModel?.includes('Local');
                  const label = lastModel
                    ? (isBana ? 'BANA'
                      : lastModel.includes('Local') ? 'Local'
                      : 'AI')
                    : 'AI';
                  return (
                    <Badge variant="default" className={`ml-2 text-[10px] ${isBana ? 'bg-green-500' : 'bg-purple-500'}`}>
                      <Brain className="h-3 w-3 mr-1" />
                      {label}
                    </Badge>
                  );
                })()}
              </div>
            </div>
            <div className="flex items-center gap-0.5">
              {messages.length > 0 && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-muted-foreground hover:text-destructive"
                  onClick={clearChat}
                  title="Clear chat"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => navigate("/system/ai-settings")}
                title="AI Settings"
              >
                <Settings className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={toggleMaximize}
                title={restoreRect ? "Restore" : "Maximize"}
              >
                {restoreRect ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 hover:text-destructive"
                onClick={() => onOpenChange(false)}
                title="Close (Esc)"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Messages Area */}
        <div className="flex-1 overflow-hidden">
          <ScrollArea className="h-full" ref={scrollRef}>
            <div className="p-4 space-y-4">
              {welcomeMessage ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-purple-500/20 to-blue-500/20 flex items-center justify-center mb-4">
                    <Sparkles className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="font-semibold mb-2">Welcome to BANA AI</h3>
                  <p className="text-sm text-muted-foreground mb-2 max-w-xs mx-auto">
                    I'm starting from scratch. Right now I can only do mathematical calculations — more skills are coming step by step.
                  </p>

                  {/* AI Mode Info */}
                  <div className="flex items-center justify-center gap-2 mb-4">
                    <Badge variant="outline" className="text-[10px]">
                      <Brain className="h-3 w-3 mr-1" />
                      BANA AI v0 · Maths only
                    </Badge>
                  </div>

                  {/* Suggested Prompts */}
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground">Try asking:</p>
                    <div className="flex flex-wrap justify-center gap-2">
                      {SUGGESTED_PROMPTS.map((prompt, i) => (
                        <Button
                          key={i}
                          variant="outline"
                          size="sm"
                          className="text-xs h-7"
                          onClick={() => handlePromptClick(prompt)}
                        >
                          {prompt}
                        </Button>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                messages.map((msg, i) => (
                  <ChatMessage key={i} message={msg} token={token} />
                ))
              )}

              {loading && (
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center text-white">
                    <Bot className="h-4 w-4" />
                  </div>
                  <div className="flex items-center gap-2 p-3 bg-muted rounded-xl rounded-bl-sm">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span className="text-sm text-muted-foreground">Thinking...</span>
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>
        </div>

        {/* Input Area */}
        <div className="p-3 border-t flex-shrink-0 bg-background">
          <form onSubmit={handleSubmit} className="flex gap-2">
            <Input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type a calculation… e.g. 18% of 25,000"
              className="flex-1"
            />
            <Button type="submit" disabled={!input.trim() || loading} size="icon">
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </form>
        </div>
    </div>
  );
}
